import React, { useMemo } from "react";
import {
  getWzEntryUILabels,
  mergeWzStartSettings,
  WZ_ENTRY_ROW_LABELS,
} from "./wzStartSettingsShared";

const CANDLE_OPTIONS = [
  { value: "3m", label: "3 분봉" },
  { value: "5m", label: "5 분봉" },
  { value: "15m", label: "15 분봉" },
  { value: "30m", label: "30 분봉" },
  { value: "1h", label: "1 시간봉" },
  { value: "4h", label: "4 시간봉" },
  { value: "12h", label: "12 시간봉" },
  { value: "1d", label: "1 일봉" },
];

const CHECKING_OPTIONS = [
  { value: -1, label: "발생" },
  { value: -2, label: "확정" },
];

function patch(setSettings, key, value) {
  setSettings((prev) => mergeWzStartSettings(prev, { [key]: value }));
}

function Field({ label, children, blurred = false }) {
  return (
    <label
      className={`admin-wz-bulk-start__field${blurred ? " is-inactive" : ""}`}
    >
      <span className="admin-wz-bulk-start__field-label">{label}</span>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, disabled, blurred = false }) {
  return (
    <select
      className={`admin-wz-field-select${blurred ? " is-inactive" : ""}`}
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      {options.map((opt) => (
        <option key={String(opt.value)} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function NumInput({ value, onChange, disabled, blurred = false, step = "any" }) {
  return (
    <input
      type="number"
      className={`admin-wz-field-input${blurred ? " is-inactive" : ""}`}
      step={step}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

function Section({ title, children }) {
  if (!children) return null;
  return (
    <div className="admin-wz-bulk-start__form-section">
      {title ? <h3 className="admin-wz-bulk-start__form-section-title">{title}</h3> : null}
      <div className="admin-wz-bulk-start__settings-grid">{children}</div>
    </div>
  );
}

function EntryGrid({
  settings,
  setSettings,
  disabled,
  usdtKeyPrefix,
  ratioKeyPrefix,
  title,
  titleClassName = "",
  isUsdtDisabled,
  isRatioDisabled,
}) {
  if (!title) return null;

  return (
    <div className="admin-wz-bulk-start__entry-block">
      <h4 className={`admin-wz-bulk-start__entry-block-title wz-entry-table__title ${titleClassName}`.trim()}>
        {title}
      </h4>
      <table className="admin-wz-bulk-start__entry-table">
        <thead>
          <tr>
            <th>순 서</th>
            <th>USDT</th>
            <th>간격(%)</th>
          </tr>
        </thead>
        <tbody>
          {WZ_ENTRY_ROW_LABELS.map((rowLabel, idx) => {
            const n = idx + 1;
            const usdtKey = `${usdtKeyPrefix}_${n}`;
            const ratioKey = `${ratioKeyPrefix}_${n}`;
            return (
              <tr key={`${usdtKeyPrefix}-${n}`}>
                <td>{rowLabel}</td>
                <td className={isUsdtDisabled ? "is-inactive" : ""}>
                  <NumInput
                    step="1"
                    value={settings[usdtKey]}
                    disabled={disabled || isUsdtDisabled}
                    blurred={isUsdtDisabled}
                    onChange={(e) => patch(setSettings, usdtKey, Number(e.target.value))}
                  />
                </td>
                <td className={isRatioDisabled ? "is-inactive" : ""}>
                  <NumInput
                    value={settings[ratioKey]}
                    disabled={disabled || isRatioDisabled}
                    blurred={isRatioDisabled}
                    onChange={(e) => patch(setSettings, ratioKey, Number(e.target.value))}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminWzBulkSettingsForm({ settings, setSettings, disabled = false }) {
  const ui = useMemo(() => getWzEntryUILabels(settings.add_position), [settings.add_position]);
  const ap = settings.add_position;

  const isLongUsdtDisabled = ap === 0 || ap === "0";
  const isLongRatioDisabled =
    ap === 0 || ap === "0" || ap === 1 || ap === "1";
  const isShortDisabled = ap === 1 || ap === "1";

  const entryModeUt = settings.entry_mode === "ut";
  const entryStandard = settings.entry_standard;
  const mixReentryApply = settings.mix_benefit_reentry === "apply";
  const occLossApply = settings.occurrence_losscut === "apply";
  const showOccurrenceLosscut = benefitStandard === "ut";
  const benefitStandard = settings.benefit_standard;

  return (
    <div className="admin-wz-bulk-start__form">
      <Section title="진입 기준">
        <Field label="진입기준">
          <Select
            value={settings.entry_mode}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "entry_mode", e.target.value)}
            options={[
              { value: "ut", label: "시그널" },
              { value: "kinpoint", label: "킨포인트" },
            ]}
          />
        </Field>
        <Field label={ui.candleText}>
          <Select
            value={settings.entry_candle}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "entry_candle", e.target.value)}
            options={CANDLE_OPTIONS}
          />
        </Field>
        <Field label="신호 기준" blurred={!entryModeUt}>
          <Select
            value={settings.candle_type_entry}
            disabled={disabled || !entryModeUt}
            blurred={!entryModeUt}
            onChange={(e) => patch(setSettings, "candle_type_entry", e.target.value)}
            options={[
              { value: "haikin", label: "하이킨아시" },
              { value: "normal", label: "일반봉" },
            ]}
          />
        </Field>
        <Field label="진입 신호 " blurred={settings.add_signals !== "ut"}>
          <Select
            value={settings.entry_checking}
            disabled={disabled || settings.add_signals !== "ut"}
            blurred={settings.add_signals !== "ut"}
            onChange={(e) => patch(setSettings, "entry_checking", Number(e.target.value))}
            options={CHECKING_OPTIONS}
          />
        </Field>
        <Field label="Sensitivity" blurred={!entryModeUt}>
          <NumInput
            value={settings.sensitivity}
            disabled={disabled || !entryModeUt}
            blurred={!entryModeUt}
            onChange={(e) => patch(setSettings, "sensitivity", Number(e.target.value))}
          />
        </Field>
        <Field label="ATR Period" blurred={!entryModeUt}>
          <NumInput
            value={settings.atr_period}
            disabled={disabled || !entryModeUt}
            blurred={!entryModeUt}
            onChange={(e) => patch(setSettings, "atr_period", Number(e.target.value))}
          />
        </Field>
      </Section>

      <Section title="보조 기준">
        <Field label="보조 모드">
          <Select
            value={settings.entry_standard}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "entry_standard", e.target.value)}
            options={[
              { value: "ut", label: "시그널" },
              { value: "candle", label: "캔들" },
              { value: "ema", label: "EMA" },
              { value: "disable", label: "미적용" },
            ]}
          />
        </Field>
        <Field label="EMA 기준봉갯수" blurred={entryStandard !== "ema"}>
          <NumInput
            value={settings.ema_standard}
            disabled={disabled || entryStandard !== "ema"}
            blurred={entryStandard !== "ema"}
            onChange={(e) => patch(setSettings, "ema_standard", Number(e.target.value))}
          />
        </Field>
        <Field label="EMA 손절적용" blurred={entryStandard !== "ema"}>
          <Select
            value={settings.ema_losscut}
            disabled={disabled || entryStandard !== "ema"}
            blurred={entryStandard !== "ema"}
            onChange={(e) => patch(setSettings, "ema_losscut", e.target.value)}
            options={[
              { value: "no", label: "미적용" },
              { value: "apply", label: "적용" },
            ]}
          />
        </Field>
        <Field label="EMA 진입 기준간격(%)" blurred={entryStandard !== "ema"}>
          <NumInput
            step="0.01"
            value={settings.ema_entry_gap_ratio ?? 0}
            disabled={disabled || entryStandard !== "ema"}
            blurred={entryStandard !== "ema"}
            onChange={(e) =>
              patch(setSettings, "ema_entry_gap_ratio", Math.max(0, Number(e.target.value)))
            }
          />
        </Field>
        <Field label="보조 Sensitivity" blurred={entryStandard !== "ut"}>
          <NumInput
            value={settings.sensitivity_standard}
            disabled={disabled || entryStandard !== "ut"}
            blurred={entryStandard !== "ut"}
            onChange={(e) => patch(setSettings, "sensitivity_standard", Number(e.target.value))}
          />
        </Field>
        <Field label="보조 ATR Period" blurred={entryStandard !== "ut"}>
          <NumInput
            value={settings.atr_period_standard}
            disabled={disabled || entryStandard !== "ut"}
            blurred={entryStandard !== "ut"}
            onChange={(e) => patch(setSettings, "atr_period_standard", Number(e.target.value))}
          />
        </Field>
        <Field label="보조 시간봉" blurred={entryStandard === "disable"}>
          <Select
            value={settings.entry_standard_candle}
            disabled={disabled || entryStandard === "disable"}
            blurred={entryStandard === "disable"}
            onChange={(e) => patch(setSettings, "entry_standard_candle", e.target.value)}
            options={CANDLE_OPTIONS}
          />
        </Field>
        <Field label="신호 기준" blurred={entryStandard === "disable"}>
          <Select
            value={settings.entry_standard_type}
            disabled={disabled || entryStandard === "disable"}
            blurred={entryStandard === "disable"}
            onChange={(e) => patch(setSettings, "entry_standard_type", e.target.value)}
            options={[
              { value: "haikin", label: "하이킨아시" },
              { value: "normal", label: "일반봉" },
            ]}
          />
        </Field>
      </Section>

      <Section title="익절 기준">
        <Field label="정리 기준" blurred={benefitStandard === "plratio"}>
          <Select
            value={settings.single_double_benefit}
            disabled={disabled || benefitStandard === "plratio"}
            blurred={benefitStandard === "plratio"}
            onChange={(e) => patch(setSettings, "single_double_benefit", e.target.value)}
            options={[
              { value: "single", label: "단독" },
              { value: "double", label: "동시" },
            ]}
          />
        </Field>
        <Field label="익절 조건">
          <Select
            value={settings.benefit_standard}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "benefit_standard", e.target.value)}
            options={[
              { value: "kinpoint", label: "킨포인트" },
              { value: "ut", label: "시그널" },
            ]}
          />
        </Field>
        <Field label="발생손절" blurred={!showOccurrenceLosscut}>
          <Select
            value={settings.occurrence_losscut === "apply" ? "apply" : "off"}
            disabled={disabled || !showOccurrenceLosscut}
            blurred={!showOccurrenceLosscut}
            onChange={(e) => {
              const next = e.target.value;
              patch(setSettings, "occurrence_losscut", next);
              if (next === "apply") {
                patch(setSettings, "mix_benefit_reentry", "off");
              }
            }}
            options={[
              { value: "off", label: "미적용" },
              { value: "apply", label: "적용" },
            ]}
          />
        </Field>
        <Field label="익절 시간봉" blurred={benefitStandard === "plratio"}>
          <Select
            value={settings.close_candle}
            disabled={disabled || benefitStandard === "plratio"}
            blurred={benefitStandard === "plratio"}
            onChange={(e) => patch(setSettings, "close_candle", e.target.value)}
            options={CANDLE_OPTIONS}
          />
        </Field>
        <Field label="신호 기준" blurred={benefitStandard !== "ut"}>
          <Select
            value={settings.candle_type_close}
            disabled={disabled || benefitStandard !== "ut"}
            blurred={benefitStandard !== "ut"}
            onChange={(e) => patch(setSettings, "candle_type_close", e.target.value)}
            options={[
              { value: "haikin", label: "하이킨아시" },
              { value: "normal", label: "일반봉" },
            ]}
          />
        </Field>
        <Field label="익절 신호">
          <Select
            value={settings.benefit_checking}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "benefit_checking", Number(e.target.value))}
            options={CHECKING_OPTIONS}
          />
        </Field>
        <Field label="Sensitivity" blurred={benefitStandard !== "ut"}>
          <NumInput
            value={settings.sensitivity_close}
            disabled={disabled || benefitStandard !== "ut"}
            blurred={benefitStandard !== "ut"}
            onChange={(e) => patch(setSettings, "sensitivity_close", Number(e.target.value))}
          />
        </Field>
        <Field label="ATR Period" blurred={benefitStandard !== "ut"}>
          <NumInput
            value={settings.atr_period_close}
            disabled={disabled || benefitStandard !== "ut"}
            blurred={benefitStandard !== "ut"}
            onChange={(e) => patch(setSettings, "atr_period_close", Number(e.target.value))}
          />
        </Field>
      </Section>

      <Section title="포지션 · 익절">
        <Field label="진입 방향 선택">
          <Select
            value={settings.direction}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "direction", e.target.value)}
            options={[
              { value: "both", label: "양방향" },
              { value: "long", label: "롱" },
              { value: "short", label: "숏" },
            ]}
          />
        </Field>
        <Field label="최소 익절률(%)" blurred={mixReentryApply}>
          <NumInput
            value={settings.both_benefit_ratio}
            disabled={disabled || mixReentryApply}
            blurred={mixReentryApply}
            onChange={(e) => patch(setSettings, "both_benefit_ratio", Number(e.target.value))}
          />
        </Field>
        <Field label="절대익절(%)">
          <NumInput
            value={settings.mix_benefit}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "mix_benefit", Number(e.target.value))}
          />
        </Field>
        <Field label="절대익절연속진입" blurred={occLossApply}>
          <select
            className="admin-wz-bulk-start__select"
            value={occLossApply ? "off" : (settings.mix_benefit_reentry === "apply" ? "apply" : "off")}
            disabled={disabled || occLossApply}
            onChange={(e) => patch(setSettings, "mix_benefit_reentry", e.target.value)}
          >
            <option value="off">미적용</option>
            <option value="apply">적용</option>
          </select>
        </Field>
      </Section>

      <div className="admin-wz-bulk__accent-block">
        <h3 className="admin-wz-bulk-start__form-section-title">종합 익절</h3>
        <div className="admin-wz-bulk-start__settings-grid admin-wz-bulk-start__settings-grid--accent">
          <Field label="종합 익절 셋팅(USDT)">
            <NumInput
              value={settings.total_benefit_usdt}
              disabled={disabled}
              onChange={(e) => patch(setSettings, "total_benefit_usdt", Number(e.target.value))}
            />
          </Field>
          <Field label="종합 익절 목표값(USDT)">
            <input
              type="text"
              className="admin-wz-field-input admin-wz-field-input--readonly"
              value="시작 시 회원별 계산"
              disabled
              readOnly
            />
          </Field>
        </div>
      </div>

      <Section title="진입 금액 · 레버리지">
        <Field label="최초진입 USDT">
          <NumInput
            value={settings.first_usdt}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "first_usdt", Number(e.target.value))}
          />
        </Field>
        <Field label="Set Leverage">
          <NumInput
            value={settings.leverage_info}
            disabled={disabled}
            onChange={(e) => patch(setSettings, "leverage_info", Number(e.target.value))}
          />
        </Field>
      </Section>

      <div className="admin-wz-bulk-start__entry-grids">
        {ui.showLongEntryGrid ? (
          <EntryGrid
            title={ui.setTextLong}
            titleClassName={ui.longTitleClass}
            usdtKeyPrefix="entry_usdt_add"
            ratioKeyPrefix="entry_ratio_add"
            settings={settings}
            setSettings={setSettings}
            disabled={disabled}
            isUsdtDisabled={isLongUsdtDisabled}
            isRatioDisabled={isLongRatioDisabled}
          />
        ) : null}
        {ui.showShortEntryGrid && ui.setTextShort ? (
          <EntryGrid
            title={ui.setTextShort}
            titleClassName={ui.shortTitleClass}
            usdtKeyPrefix="entry_usdt"
            ratioKeyPrefix="entry_ratio"
            settings={settings}
            setSettings={setSettings}
            disabled={disabled}
            isUsdtDisabled={isShortDisabled}
            isRatioDisabled={isShortDisabled}
          />
        ) : null}
      </div>
      <p className="admin-wz-bulk-start__hint">
        SetpageWz에 없는 항목(entry_signal, add_position, MA/RSI 등)은 wz 프리셋으로만 반영됩니다.
      </p>
    </div>
  );
}
